import { CommonModule } from '@angular/common';
import { Component, effect, EventEmitter, inject, Injector, Input, model, OnDestroy, Output, Signal, signal } from '@angular/core';

import { MetricExtension } from 'src/app/classes/viewer/metrics/extension';
import { OriginExtension } from 'src/app/classes/viewer/origin/extension';
import { PlanningExtension } from 'src/app/classes/viewer/planning-model/extension';

import * as ForgeTools from 'src/app/classes/shared/threeDTools';
import { Fragment } from 'src/app/interfaces/api/fragment';
import { Model } from 'src/app/interfaces/api/model';

import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import {
  Extension,
  ViewerInitializedEvent,
  ViewerModule,
  ViewerOptions
} from "ng2-adsk-forge-viewer";
import { Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { Pop } from 'src/app/classes/shared/pop';
import { ALL_ROLES, GalleryComponent, TagDisplayConfig } from 'src/app/components/shared/gallery/gallery.component';
import { Attachment } from 'src/app/interfaces/api/attachment';
import { Metric } from 'src/app/interfaces/api/metric';
import { Planning } from 'src/app/interfaces/api/planning';
import { Project } from 'src/app/interfaces/api/project';
import { PlanningTask, PutTask } from 'src/app/interfaces/api/task';
import { User } from 'src/app/interfaces/api/user';
import { MetricService } from 'src/app/services/api/metrics/metric.service';
import { ModelService } from 'src/app/services/api/models/model.service';
import { PlanningService } from 'src/app/services/api/plannings/planning.service';
import { TrackService } from 'src/app/services/api/tracks/track.service';
import { AuthService } from 'src/app/services/core/auth.service';
import { PlanningStore } from 'src/app/services/stores/planning.store';

@Component({
  selector: 'app-viewer',
  templateUrl: './viewer.component.html',
  styleUrls: ['./viewer.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ViewerModule
  ]
})
export class ViewerComponent implements OnDestroy {
  // Services
  private readonly planningStore: PlanningStore = inject(PlanningStore);
  private readonly injector: Injector = inject(Injector);
  private readonly planningService: PlanningService = inject(PlanningService);
  private readonly metricService: MetricService = inject(MetricService);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly trackService: TrackService = inject(TrackService);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly pop: Pop = inject(Pop);
  private readonly modelService: ModelService = inject(ModelService);
  private readonly authService: AuthService = inject(AuthService);

  // Component API
  @Input() modelId: string = "";
  @Input() user!: User;
  @Input() project!: Project;
  @Input() task!: PlanningTask;
  @Input() startDate!: string;
  @Input() endDate!: string;
  @Input() context: string = "planning";
  @Input() model = signal<Model | null>(null);
  @Input() selectedMetrics: Signal<Metric[]> = this.planningStore.selectedMetrics;
  @Input() selectedFragments: Signal<Fragment[]> = this.planningStore.selectedFragments;
  @Output() onTaskUpdate: EventEmitter<PlanningTask> = new EventEmitter();
  @Output() onTaskSelected: EventEmitter<PlanningTask> = new EventEmitter();
  @Output() onLoaded3D: EventEmitter<any> = new EventEmitter();

  // Signals
  readonly planning: Signal<Planning> = this.planningStore.planning;
  readonly currentTask: Signal<PlanningTask | null> = this.planningStore.currentTask;
  currentDate: Signal<string> = model<string>(new Date().toISOString());
  readonly currentUser: Signal<User> = this.authService.currentUser;

  // Variables
  destroy = new Subject<void>();
  viewerOptions3d: any;
  viewer!: Autodesk.Viewing.Viewer3D;
  public viewerOptions!: ViewerOptions;
  // planning: any;
  planningExt: any;

  originExt!: any;
  metricExt!: any;
  loadingFragment: boolean = false;
  parsingFragment: boolean = false;
  rebuildFragment: boolean = false;
  // loadingPlanning: boolean = false;
  updatePlanningSub: any;
  updateProgress: string = '';
  startBarPosition: number = 50;
  autolinkAvailable: boolean = false;

  dbFragments: any[] = [];
  realtime: boolean = true;
  tagDisplayConfig: TagDisplayConfig = {
    visibility: {
      crane: { ai: ALL_ROLES, manual: ALL_ROLES },
      worker: { ai: ALL_ROLES, manual: ALL_ROLES },
      zone: { ai: ALL_ROLES, manual: ALL_ROLES },
      action: { ai: ALL_ROLES, manual: ALL_ROLES },
      risk: { ai: ['dev'], manual: ['dev', 'admin'] }
    }
  };
  fragments: Fragment[] = [];

  constructor() {
    effect(() => {
      const fragments = this.planningStore.fragments();
      const model = this.model();

      this.fragments = fragments;

      if (!this.viewer && model)
        this.initializeViewer3D();
    });
  }

  initializeViewer3D() {
    let model = this.model();

    if (!model) return;

    if (model.origin.position.lat != undefined && model.origin.position.lon != undefined) {
      model.origin.projectLatLonXY = ForgeTools.getXYFromLatLonAlt2(model.origin.position.lat, model.origin.position.lon, 0) as [number, number];
    }

    if (model.origin.position.x != undefined && model.origin.position.y != undefined) {
      model.origin.projectXY = [parseFloat(model.origin.position.x.toString()), parseFloat(model.origin.position.y.toString())] as [number, number];
    } else {
      model.origin.projectXY = [0, 0] as [number, number]
    }

    model.offset = {
      x: 0,
      y: 0,
      z: 0
    }

    this.model.set(model);
    let theme = this.currentUser().theme == "dark" ? "dark-theme" : "bim-theme";

    this.viewerOptions3d = {
      initializerOptions: {
        // env: 'Local',
        env: 'AutodeskProduction',
        getAccessToken: (onGetAccessToken: (token: string, expire: number) => void) => {
          this.modelService.getToken().pipe(takeUntil(this.destroy)).subscribe(res => {
            onGetAccessToken(res.access_token, res.expires_in);
          })
        },
        api: 'derivativeV2',
        // extensions: [PlanningExtension.extensionName, OriginExtension.extensionName, ZoneExtension.extensionName, UnitExtension.extensionName],
        extensions: [
          PlanningExtension.extensionName,
          MetricExtension.extensionName,
          OriginExtension.extensionName
        ],
        theme
      },
      viewerConfig: {
        // extensions: [PlanningExtension.extensionName, OriginExtension.extensionName, ZoneExtension.extensionName, UnitExtension.extensionName],
        extensions: [
          PlanningExtension.extensionName,
          MetricExtension.extensionName,
          OriginExtension.extensionName
        ],
        theme
      },
      onViewerScriptsLoaded: () => {
        Extension.registerExtension(PlanningExtension.extensionName, PlanningExtension);
        Extension.registerExtension(OriginExtension.extensionName, OriginExtension);
        Extension.registerExtension(MetricExtension.extensionName, MetricExtension);
        // Extension.registerExtension(UnitExtension.extensionName, UnitExtension);
      },
      onViewerInitialized: (args: ViewerInitializedEvent) => this.loadDocument(args)
    };

  }

  public loadDocument(args: ViewerInitializedEvent) {
    let model = this.model();
    if (!model) return;

    this.viewer = args.viewer;

    this.viewer.addEventListener(Autodesk.Viewing.TOOLBAR_CREATED_EVENT, () => {
      let offset = this.viewer.model.getData().globalOffset

      if (offset && offset.x < 10 ** 5) {
        model.offset = offset;
        this.model.set(model)
      }

      this.initExtensions();
    })

    args.viewerComponent.DocumentId = model.urn || '';
  }

  initExtensions() {
    this.metricExt = this.viewer.loadedExtensions[MetricExtension.extensionName]
    this.planningExt = this.viewer.loadedExtensions[PlanningExtension.extensionName]
    this.originExt = this.viewer.loadedExtensions[OriginExtension.extensionName];

    if (!this.metricExt || !this.planningExt || !this.originExt) return;

    const commons = {
      planningService: this.planningService,
      planningStore: this.planningStore,
      metricService: this.metricService,
      trackService: this.trackService,
      translate: this.translate,
      startDate: this.startDate,
      endDate: this.endDate,
      model: this.model() as Model,
      context: this.context,
    }

    this.planningExt.metricExt = this.metricExt;
    this.planningExt.init({
      ...commons,
      injector: this.injector,
      event: (event: string, message: any) => {
        console.log("Event from planning extension:", event, message);
      }
    })

    this.planningExt.setFragColorAtDate(this.selectedFragments(), true)
    // this.planningExt.viewer = this.viewer;
    // this.planningExt.onPlanningReady = () => this.onPlanningReady();
    // this.planningExt.onLinkMetric = (fragment: Fragment) => this.onLinkMetric(fragment);
    // this.planningExt.onImageClick = (event: any) => {
    //   let index = 0;
    //   let attachments = [event.target.data.attachment]
    //   this.onImageClick(index, attachments)
    // };

    this.planningExt.linkFragsToTask = (frags: Fragment[], task: PlanningTask | null = null) => this.linkFragsToTask(frags, task);
    this.metricExt.planningExt = this.planningExt;

    this.metricExt.init({
      ...commons,
      injector: this.injector,
      event: (event: string, message: any) => {
        console.log("Event from planning extension:", event, message);
      }
    });

    // this.originExt.init({
    //   ...commons,
    //   planningExt: this.planningExt,
    //   metricExt: this.metricExt,
    // })

    // this.planningExt.init();
    // this.metricExt.init();
    // this.originExt.init()

    this.viewer.impl.invalidate(true, true, true)
    this.onLoaded3D.emit(this.viewer);

    // this.viewer.setProgressiveRendering(false);
    // this.viewer.navigation.toPerspective();
  }

  ngOnDestroy() {
    this.destroy.next();
    this.destroy.complete();
  }

  onPlanningReady() {
    this.parsingFragment = false;

    // if (this.metricExt)
    //   // this.metricExt.updateFragments();
  }

  // onLinkMetric(fragment: Fragment) {

  //   const dialogRef = this.dialog.open(MetricListDialogComponent, {
  //     width: "80%",
  //     height: "80%",
  //     data: {
  //       fragment,
  //       planning: this.planning, // Original this.planning variable
  //       task: this.tasks.find(t => t.fragments.indexOf(fragment.id) >= 0)
  //     }
  //   });

  //   dialogRef.afterClosed().subscribe((res) => {

  //     if (res && res.selectedMetrics) {
  //       //this.
  //       if (!fragment.metrics) fragment.metrics = [];
  //       fragment.metrics.length = 0;
  //       res.selectedMetrics.forEach((m: Metric) => {
  //         let startDate = moment(m.payload?.startDate);
  //         let endDate = moment(m.payload?.endDate);

  //         let newMetric: NonNullable<Fragment['metrics']>[0] = {
  //           id: m._id || m.id,
  //           name: m.name,
  //           startDate: startDate.toISOString(),
  //           endDate: endDate.toISOString(),
  //           midDate: moment.unix(Math.floor((startDate.unix() + endDate.unix()) / 2)).toISOString(),
  //           payload: m.payload
  //         }

  //         fragment.metrics!.push(newMetric);
  //       })
  //       let update = {
  //         id: fragment.id,
  //         metrics: fragment.metrics
  //       }


  //       this.planningService.updateFragment(update).pipe(takeUntil(this.destroy)).subscribe(res => {
  //         console.log("metric updated")
  //       })
  //     }
  //   });
  // }

  onImageClick(index: number, attachments: Attachment[]) {
    let attachment = attachments[index];
    if (!attachment) return;

    const dialogRef = this.dialog.open(GalleryComponent, {
      width: "auto",
      height: "auto",
      data: {
        index,
        id: attachment._id || attachment.id,
        urls: attachments.map((a: Attachment) => a.url),
        startDate: attachment.createdDate,
        endDate: moment(attachment.createdDate).add(1, "minute").toISOString(),
        boxes: attachment.predictions,
        tagDisplayConfig: this.tagDisplayConfig
      }
    });

    dialogRef.afterClosed().subscribe((res) => {
      // if (res && res.updated) {
      //   this.getMetrics();
      // }
    });
  }

  // setFragments() {
  //   if (this.planningExt) {
  //     console.time("setFragments")
  //     this.planningExt.setDbFragments(this.dbFragments).then(() => {

  //       // this.parsingFragment = false;
  //       // let taskToRebuild = this.planningExt.checkTasks()
  //       // if (taskToRebuild.length) {

  //       //   this.rebuildFragment = true;
  //       //   this.planningsService.rebuildTaskFragments({ planning: this.planning.id, model: this.model.id }).pipe(takeUntil(this.destroy)).subscribe(result => {
  //       //     this.rebuildFragment = false;
  //       //     this.loadPlanning(this.planningId);
  //       //   }, error => {
  //       //     this.rebuildFragment = false;

  //       //   })

  //       // }
  //       this.dbFragments = this.planningExt.getDbFragments();
  //       console.timeEnd("setFragments")
  //       this.autolinkAvailable = !!this.dbFragments.find(f => f.taskUUID) && this.planning.tasks.length;
  //       let taskLinked = this.planning.tasks.find(t => t.fragments && t.fragments.length)
  //       if (this.autolinkAvailable && !taskLinked) {
  //         this.linkFragments('propose_autolink')
  //       }
  //     }, err => {
  //       this.parsingFragment = false;
  //       console.error(err)
  //     })
  //   }
  // }

  linkFragments(message = 'confirm_autolink') {
    this.pop.confirm(this.translate.instant('planning.' + message), (confirm: any) => {
      if (confirm.ok) {
        this.rebuildFragment = true;

        this.planningService.autoLinkTaskFragments({ planning: this.planningStore.planning().id, model: this.modelId }).pipe(takeUntil(this.destroy)).subscribe(result => {
          this.rebuildFragment = false;
        }, error => {
          this.rebuildFragment = false;
        })
      }
    });
  }

  linkFragsToTask(frags: Fragment[], task: PlanningTask | null = null) {
    if (!task) {
      task = this.task;
    }

    if (task) {
      const allTasks = this.planningStore.tasks();

      frags.forEach(f => {
        if (f.volume) {
          task.volume += f.volume;
        }

        let linkedTask = allTasks.find((t: PlanningTask) => t.fragmentDbIds?.includes(f.dbId!));

        if (linkedTask && task.id != linkedTask.id) {
          const foundTask = allTasks.find((t: PlanningTask) => t.id == linkedTask!.id);

          if (foundTask) {
            linkedTask = foundTask;
            let fIndex = linkedTask.fragments?.findIndex((_f: string) => _f == f.id) ?? -1;
            let fDbIndex = linkedTask.fragmentDbIds?.findIndex((_f: number) => _f == f.dbId) ?? -1;

            if (fIndex >= 0 && linkedTask.fragments)
              linkedTask.fragments.splice(fIndex, 1);

            if (fDbIndex >= 0 && linkedTask.fragmentDbIds)
              linkedTask.fragmentDbIds.splice(fDbIndex, 1);

            this.onTaskUpdate.emit(linkedTask);

            this.planningService.updateTaskV2(this.toSaveTask(linkedTask)).pipe(takeUntil(this.destroy)).subscribe({
              next: () => console.log('✅ Tâche liée sauvegardée'),
              error: (err) => console.error('❌ Erreur sauvegarde tâche liée:', err)
            });
          }
        }
      })

      if (!task.fragments)
        task.fragments = [];

      const newFragIds = frags
        .filter(f => !task!.fragments?.includes(f.id))
        .map(f => f.id);

      const newFragDbIds = frags
        .filter(f => !task!.fragmentDbIds?.includes(f.dbId!))
        .map(f => f.dbId)
        .filter((id): id is number => id !== undefined);

      task.fragments = [...(task.fragments || []), ...newFragIds];
      task.fragmentDbIds = [...(task.fragmentDbIds || []), ...newFragDbIds];

      this.onTaskUpdate.emit({ ...task, currentTask: true } as PlanningTask);
      this.planningExt!.getSelection();

      this.planningService.updateTaskV2(this.toSaveTask(task)).pipe(takeUntil(this.destroy)).subscribe({
        next: () => console.log('✅ Tâche principale sauvegardée'),
        error: (err) => console.error('❌ Erreur sauvegarde tâche principale:', err)
      });
    }
  }

  updateFragments() {
    this.loadingFragment = true;
    let frags = this.planningExt!.getFragmentZones();
    return this.planningService.updateFragments(frags).pipe(take(1))
  }

  private toSaveTask(task: PlanningTask): PutTask {
    return {
      id: task.id,
      number: task.number,
      startDate: task.startDate,
      endDate: task.endDate,
      duration: task.duration ? String(task.duration) : undefined,
      progress: (typeof task.progress === 'number' && !isNaN(task.progress)) ? String(task.progress) : undefined,
      parent: task.parent ? String(task.parent) : undefined,
      fragments: task.fragments,
      fragmentDbIds: task.fragmentDbIds,
      text: task.text,
      target: task.target,
      volume: task.volume,
      planning: task.planning,
    } as PutTask;
  }

  refreshFragDb(): Observable<{ status: string; progress: number; total: number }> {
    return this.planningExt!.createFragmentsDbAsync();
  }

  // setObjectVisibility(date = "") {
  //   let fragmentIds:  string[] = [];
  //   date = date || this.currentDate()
  //   this.tasks.forEach((t, i) => {
  //     if (t.startDate && moment(date).isAfter(t.startDate)) {
  //       fragmentIds = fragmentIds.concat(t.fragments ?? []);
  //     } else if (t.firstUpdate && moment(date).isAfter(t.firstUpdate)) {
  //       fragmentIds = fragmentIds.concat(t.fragments ?? []);
  //     }
  //   })
  //   if (this.planningExt) {
  //     this.planningExt.realtime = this.realtime;
  //     let fragment
  //     this.planningExt.setFragColorAtDate((fragments as string[]), date, true);
  //   }
  // }

  setMetricVisibilityByDate() {
    this.metricExt.setMetricAtDate(this.currentDate());
  }

  setMetricVisibilityByTasks(tasks: PlanningTask[] = []) {
    let startDate: string | number | undefined = this.currentDate(),
      endDate: string | number | undefined;

    tasks.forEach(t => {
      if (!startDate && t.startDate) startDate = t.startDate
      else if (!endDate && t.endDate) endDate = t.endDate
      else if (t.startDate && startDate && moment(t.startDate).isBefore(startDate)) startDate = t.startDate
      else if (t.endDate && endDate && moment(t.endDate).isAfter(endDate)) endDate = t.endDate
    });

    this.metricExt.setMetricAtDate(startDate as any, endDate as any);
  }
}
